import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FileText, ExternalLink } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { useSession } from '@/app/session';
import { clinicalApi } from '@/api/clinical.api';
import { filesApi } from '@/api/files.api';
import { imagingApi } from '@/api/imaging.api';
import './PatientPeek.css';

/**
 * A patient's files, documents and radiographs, in a modal — so from a chat with
 * the assistant a person can look at what the answer referred to without leaving
 * for the patient page. It reuses the record the page already caches
 * (`['record', patientId]`) and the imaging list, so opening it beside an open
 * record costs nothing. Files open in a new tab through a freshly signed URL,
 * exactly as the record's own gallery does; nothing here writes. Which sections
 * appear follows the asker's permissions, the same as the record screen.
 */
export function PatientPeek({
  patientId,
  name,
  onClose,
}: {
  patientId: string;
  name: string;
  onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  const { can, hasFeature } = useSession();

  const canRecord = can('fisa.vede');
  const canImaging = can('imagistica.vede') && hasFeature('imaging');

  const record = useQuery({
    queryKey: ['record', patientId],
    queryFn: () => clinicalApi.record(patientId),
    enabled: canRecord,
  });
  const studies = useQuery({
    queryKey: ['imaging', 'patient', patientId],
    queryFn: () => imagingApi.forPatient(patientId),
    enabled: canImaging,
  });

  const openFile = async (path: string | null) => {
    if (!path) return;
    try {
      const { url } = await filesApi.url(path);
      window.open(url, '_blank', 'noopener');
    } catch {
      /* a broken link is not worth a dialog here */
    }
  };

  const images = record.data?.images ?? [];
  const documents = record.data?.documents ?? [];
  const studyRows = studies.data ?? [];
  // `isPending`, not `isLoading`: a query paused after an error is not loading,
  // and we must not fall through to "no files" about data we never received.
  const loading = (canRecord && record.isPending) || (canImaging && studies.isPending);
  const nothing =
    !loading && images.length === 0 && documents.length === 0 && studyRows.length === 0;

  return (
    <Modal title={name} width={640} onClose={onClose}>
      <div className="ppeek">
        {loading ? <p className="ppeek__muted">{t('peekIncarca')}</p> : null}
        {nothing ? <p className="ppeek__muted">{t('peekNimic')}</p> : null}

        {studyRows.length > 0 ? (
          <section className="ppeek__sec">
            <h3 className="ppeek__h">{t('peekStudii')}</h3>
            <div className="ppeek__grid">
              {studyRows.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className="ppeek__tile"
                  onClick={() =>
                    window.open(`/patients/${patientId}/studies/${s.id}`, '_blank', 'noopener')
                  }
                  title={`${s.kind} · ${s.studyDate}`}
                >
                  {s.previewUrl ? (
                    <img src={s.previewUrl} alt="" loading="lazy" />
                  ) : (
                    <span className="ppeek__ph">{s.kind}</span>
                  )}
                  <span className="ppeek__cap">{s.studyDate}</span>
                </button>
              ))}
            </div>
          </section>
        ) : null}

        {images.length > 0 ? (
          <section className="ppeek__sec">
            <h3 className="ppeek__h">{t('peekImagini')}</h3>
            <div className="ppeek__grid">
              {images.map((im) => (
                <button
                  key={im.id}
                  type="button"
                  className="ppeek__tile"
                  onClick={() => openFile(im.filePath)}
                  title={im.name ?? ''}
                >
                  <FileThumb path={im.filePath} alt={im.name ?? ''} />
                  <span className="ppeek__cap">{im.name ?? im.imgDate ?? ''}</span>
                </button>
              ))}
            </div>
          </section>
        ) : null}

        {documents.length > 0 ? (
          <section className="ppeek__sec">
            <h3 className="ppeek__h">{t('peekDocumente')}</h3>
            <ul className="ppeek__docs">
              {documents.map((d) => (
                <li key={d.id}>
                  <button type="button" onClick={() => openFile(d.filePath)}>
                    <FileText size={16} aria-hidden="true" />
                    <span className="ppeek__docname">{d.name}</span>
                    {d.sizeLabel ? <span className="ppeek__muted">{d.sizeLabel}</span> : null}
                    <ExternalLink size={14} aria-hidden="true" className="ppeek__ext" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </Modal>
  );
}

/** A gallery image's thumbnail — the signed URL is minted on demand, then cached. */
function FileThumb({ path, alt }: { path: string | null; alt: string }): React.JSX.Element {
  const q = useQuery({
    queryKey: ['file-url', path],
    queryFn: () => filesApi.url(path as string),
    enabled: !!path,
    staleTime: 4 * 60 * 1000,
  });
  if (!q.data?.url) return <span className="ppeek__ph" aria-hidden="true" />;
  return <img src={q.data.url} alt={alt} loading="lazy" />;
}
